import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// ---- Filtro global de transportadora (sincronizado via setTransportadoraCnpj) ----
let _transportadoraCnpj = "";

export const setTransportadoraCnpj = (cnpj) => {
  _transportadoraCnpj = cnpj || "";
};
export const getTransportadoraCnpj = () => _transportadoraCnpj;

const _routesAcceptingCnpjFilter = [
  "/ocoren/movimentacoes",
  "/ocoren/notas",
  "/ocoren/arquivos",
  "/ocoren/diffs",
  "/ocoren/stats",
  "/ocoren/export",
  "/ocoren/atraso",
  "/ocoren/ctes",
];

api.interceptors.request.use((cfg) => {
  if (!_transportadoraCnpj) return cfg;
  const url = cfg.url || "";
  const accepts = _routesAcceptingCnpjFilter.some((r) => url.includes(r));
  if (!accepts) return cfg;
  cfg.params = cfg.params || {};
  if (cfg.params.cnpj_transportadora === undefined) {
    cfg.params.cnpj_transportadora = _transportadoraCnpj;
  }
  return cfg;
});

// Lê o valor inicial do localStorage no momento da carga do módulo (síncrono)
try {
  const stored = localStorage.getItem("ocoren.transp.cnpj");
  if (stored) _transportadoraCnpj = stored;
  // Sobrescreve com query param se houver
  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get("transp");
  if (fromUrl) _transportadoraCnpj = fromUrl;
} catch {
  /* localStorage/URL indisponível, ignorar */
}

export const uploadOcoren = async (files) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  const res = await api.post("/ocoren/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const getArquivos = async () => {
  const res = await api.get("/ocoren/arquivos");
  return res.data;
};

export const deleteArquivo = async (id) => {
  const res = await api.delete(`/ocoren/arquivos/${id}`);
  return res.data;
};

export const getMovimentacoes = async (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) params[k] = v;
  });
  const res = await api.get("/ocoren/movimentacoes", { params });
  return res.data;
};

export const getStats = async () => {
  const res = await api.get("/ocoren/stats");
  return res.data;
};

export const getStatsCategorias = async () => {
  const res = await api.get("/ocoren/stats/categorias");
  return res.data;
};

export const getNotas = async (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) params[k] = v;
  });
  const res = await api.get("/ocoren/notas", { params });
  return res.data;
};

// ============== CT-e (Conhecimento de Transporte) — vínculo via MySQL externo ==============
export const getCtes = async (filters = {}) => {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) params[k] = v;
  });
  const res = await api.get("/ocoren/ctes", { params });
  return res.data;
};

export const getCteDetalhe = async (numero_cte, cnpj_transportadora) => {
  const params = {};
  if (cnpj_transportadora) params.cnpj_transportadora = cnpj_transportadora;
  const res = await api.get(`/ocoren/ctes/${encodeURIComponent(numero_cte)}`, { params });
  return res.data;
};

export const getCtesExportUrl = (filters = {}) => {
  const merged = { ...filters };
  if (!merged.cnpj_transportadora) {
    const global = getTransportadoraCnpj();
    if (global) merged.cnpj_transportadora = global;
  }
  const params = new URLSearchParams();
  Object.entries(merged).forEach(([k, v]) => {
    if (k === "skip" || k === "limit") return;
    if (v !== "" && v !== null && v !== undefined) params.append(k, v);
  });
  const qs = params.toString();
  return `${API}/ocoren/ctes/export${qs ? `?${qs}` : ""}`;
};

export const getDiffs = async (limit = 5000) => {
  const res = await api.get("/ocoren/diffs", { params: { limit } });
  return res.data;
};

export const getDiffDetail = async (arquivoId) => {
  const res = await api.get(`/ocoren/diffs/${arquivoId}`);
  return res.data;
};

export const getDiffTotais = async () => {
  const res = await api.get("/ocoren/diffs/stats/totais");
  return res.data;
};

export const getFtpStatus = async () => {
  const res = await api.get("/ocoren/ftp/status");
  return res.data;
};

export const ftpSyncNow = async (maxFiles) => {
  const params = {};
  if (maxFiles) params.max_files = maxFiles;
  const res = await api.post("/ocoren/ftp/sync", null, { params });
  return res.data;
};

// ============== Regras de Classificação ==============
export const getRules = async () => {
  const res = await api.get("/ocoren/rules");
  return res.data;
};

export const createRule = async (rule) => {
  const res = await api.post("/ocoren/rules", rule);
  return res.data;
};

export const updateRule = async (id, partial) => {
  const res = await api.patch(`/ocoren/rules/${id}`, partial);
  return res.data;
};

export const deleteRule = async (id) => {
  const res = await api.delete(`/ocoren/rules/${id}`);
  return res.data;
};

export const getInboxOutros = async (limit = 100) => {
  const res = await api.get("/ocoren/inbox-outros", { params: { limit } });
  return res.data;
};

// ============== Atraso / SLA, Prazos e Eventos Manuais ==============
export const getAtraso = async (somenteAtraso = true) => {
  const res = await api.get("/ocoren/atraso", {
    params: { somente_atraso: somenteAtraso },
  });
  return res.data;
};

export const getPrazo = async (numero_nota, cnpj_cliente) => {
  const params = { numero_nota };
  if (cnpj_cliente) params.cnpj_cliente = cnpj_cliente;
  const res = await api.get("/ocoren/notas/prazo", { params });
  return res.data;
};

export const setPrazo = async (payload) => {
  const res = await api.post("/ocoren/notas/prazo", payload);
  return res.data;
};

export const deletePrazo = async (numero_nota, cnpj_cliente) => {
  const params = { numero_nota };
  if (cnpj_cliente) params.cnpj_cliente = cnpj_cliente;
  const res = await api.delete("/ocoren/notas/prazo", { params });
  return res.data;
};

export const addEventoManual = async (payload) => {
  const res = await api.post("/ocoren/notas/evento-manual", payload);
  return res.data;
};

export const getRegrasAtraso = async () => {
  const res = await api.get("/ocoren/regras-atraso");
  return res.data;
};

export const createRegraAtraso = async (payload) => {
  const res = await api.post("/ocoren/regras-atraso", payload);
  return res.data;
};

export const updateRegraAtraso = async (id, partial) => {
  const res = await api.patch(`/ocoren/regras-atraso/${id}`, partial);
  return res.data;
};

export const deleteRegraAtraso = async (id) => {
  const res = await api.delete(`/ocoren/regras-atraso/${id}`);
  return res.data;
};

// ============== Comprovantes de Entrega ==============
export const uploadComprovantes = async (numero_nota, cnpj_cliente, fileList) => {
  const form = new FormData();
  form.append("numero_nota", numero_nota);
  if (cnpj_cliente) form.append("cnpj_cliente", cnpj_cliente);
  Array.from(fileList).forEach((f) => form.append("files", f));
  const res = await api.post("/ocoren/notas/comprovante", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const getComprovantes = async (numero_nota, cnpj_cliente) => {
  const params = { numero_nota };
  if (cnpj_cliente) params.cnpj_cliente = cnpj_cliente;
  const res = await api.get("/ocoren/notas/comprovante", { params });
  return res.data;
};

export const getComprovantesResumo = async () => {
  const res = await api.get("/ocoren/notas/comprovantes-resumo");
  return res.data;
};

export const deleteComprovante = async (id) => {
  const res = await api.delete(`/ocoren/comprovante/${id}`);
  return res.data;
};

export const comprovanteDownloadUrl = (id) =>
  `${API}/ocoren/comprovante/${id}/download`;

export const comprovantesZipUrl = (numero_nota, cnpj_cliente) => {
  const qs = new URLSearchParams({ numero_nota });
  if (cnpj_cliente) qs.append("cnpj_cliente", cnpj_cliente);
  return `${API}/ocoren/notas/comprovantes/zip?${qs.toString()}`;
};

export const buildExportUrl = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined) params.append(k, v);
  });
  const qs = params.toString();
  return `${API}/ocoren/export${qs ? `?${qs}` : ""}`;
};

// ============== Ações do Módulo de CT-e (Etapa Manual, Prazo e Comprovante) ==============
export const addEventoManualCte = async (payload) => {
  const res = await api.post("/ocoren/ctes/evento-manual", payload);
  return res.data;
};

export const setPrazoCte = async (payload) => {
  const res = await api.post("/ocoren/ctes/prazo", payload);
  return res.data;
};

export const uploadComprovantesCte = async (numero_cte, numeros_notas, fileList) => {
  const form = new FormData();
  form.append("numero_cte", numero_cte);
  form.append("numeros_notas", JSON.stringify(numeros_notas));
  Array.from(fileList).forEach((f) => form.append("files", f));
  const res = await api.post("/ocoren/ctes/comprovante", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const formatCNPJ = (cnpj) => {
  if (!cnpj) return "—";
  const c = String(cnpj).replace(/\D/g, "");
  if (c.length !== 14) return cnpj;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12, 14)}`;
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    }
    const d = new Date(iso);
    return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
  } catch {
    return iso;
  }
};

export const gerarLinkRastreio = async (payload) => {
  const res = await api.post("/ocoren/rastreio/gerar", payload);
  return res.data;
};

export const getRastreioPublico = async (token) => {
  const res = await axios.get(`${BACKEND_URL}/api/rastreio/publico/${token}`);
  return res.data;
};

export const publicComprovanteDownloadUrl = (comprovante_id) => {
  return `${BACKEND_URL}/api/rastreio/publico/comprovante/${comprovante_id}/download`;
};
